import { isDefaultFilterSelected, useUserStore } from '@local/shared';
import { BreadcrumbsLinkProps, Flex, Layout, Link, Page, Text } from '@servicetitan/anvil2';
import { useDependencies } from '@servicetitan/react-ioc';
import { ChatParticipantIcon } from '@servicetitan/titan-chat-ui-common';
import { CHATBOT_UI_STORE_TOKEN, ChatbotCustomizations } from '@servicetitan/titan-chatbot-api';
import { Chatbot as ChatbotComponent } from '@servicetitan/titan-chatbot-ui-anvil2';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import * as Styles from './chatbot-provided.module.less';

interface LocationState {
    query?: string;
}

export const ChatbotProvided: FC = observer(() => {
    const [chatbotUiStore] = useDependencies(CHATBOT_UI_STORE_TOKEN);
    const userStore = useUserStore();
    const location = useLocation<LocationState>();
    const history = useHistory<LocationState>();
    const incomingQuery = location.state?.query ?? '';
    const hasAccess = userStore.user?.hasAccess;
    const customizations = useMemo<ChatbotCustomizations>(
        () => ({
            filters: {
                enabled: true,
                isDefaultSelected: isDefaultFilterSelected,
            },
            feedback: {
                showGuardrailFeedback: true,
            },
        }),
        []
    );

    const breadcrumbs: BreadcrumbsLinkProps[] | undefined = useMemo(
        () =>
            hasAccess ? [{ href: '/', children: 'Home' }, { children: `Ask Atlas` }] : undefined,
        [hasAccess]
    );

    // Remove query from history state after first render
    useEffect(() => {
        if (location.state?.query) {
            const { query, ...rest } = location.state;
            history.replace({ ...location, state: Object.keys(rest).length ? rest : undefined });
        }
    }, [location, history]);

    const handleReady = useCallback(() => {
        if (!incomingQuery) {
            return;
        }
        chatbotUiStore.setMessages([]);
        chatbotUiStore.sendMessageText(incomingQuery);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Page className={Styles.page}>
            <Page.Header title="" breadcrumbs={breadcrumbs} data-cy="ish-page-chatbot-header" />
            <Page.Content className={Styles.pageContent}>
                <Layout className={Styles.fullHeight} data-cy="ish-page-chatbot-content">
                    <Layout.Item span={12}>
                        <Flex
                            direction="column"
                            className={classNames(Styles.fullHeight, 'p-block-end-8')}
                        >
                            <Flex direction="column" grow={1}>
                                <ChatbotComponent
                                    className={Styles.fullHeight}
                                    customizations={customizations}
                                    botName="Atlas"
                                    botIcon={ChatParticipantIcon.Atlas}
                                    onReady={handleReady}
                                />
                            </Flex>
                            <Text className="p-inline-6" size="small" subdued>
                                Atlas is TI-powered, subject to our privacy policy.{' '}
                                <Link
                                    appearance="secondary"
                                    href="https://help.servicetitan.com/landing-page/ti-assist"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Learn more
                                </Link>
                            </Text>
                        </Flex>
                    </Layout.Item>
                </Layout>
            </Page.Content>
        </Page>
    );
});
